import QRCode from "qrcode"
import axios from "axios"
import ApplicationState from "../../../../Stores/applicationStates"

/**
 * Function to capitalize the first letter of each entry in an array of strings.
 * @param arr array in which the entries to be capitalised
 */
export const capitalizeArrayEntries = (arr) => {
  // Use the `map` method to loop through each entry in the array.
  return arr.map((entry) => {
    // Check if the entry is not a string (e.g., number, object, etc.).
    // If it's not a string, return the entry as it is without modification.
    if (typeof entry !== "string") return entry

    // For string entries, capitalize the first letter using `charAt(0).toUpperCase()`.
    // Then, concatenate the rest of the string using `slice(1)`.
    return entry.charAt(0).toUpperCase() + entry.slice(1)
  })
}

/**
 * Function to capitalize the first letter of each entry in an array of strings.
 * @param arr array in which the entries to be capitalised
 */
export const lowerCaseArrEntries = (arr) => {
  // Use the `map` method to loop through each entry in the array.
  return arr.map((entry) => {
    // Check if the entry is not a string (e.g., number, object, etc.).
    // If it's not a string, return the entry as it is without modification.
    if (typeof entry !== "string") return entry

    // For string entries, capitalize the first letter using `charAt(0).toUpperCase()`.
    // Then, concatenate the rest of the string using `slice(1)`.
    return entry.charAt(0).toLowerCase() + entry.slice(1)
  })
}

/**
 * Loads an image from the given URI and returns it as a Promise.
 *
 * @param {string} uri - The URI of the image to be loaded.
 * @returns {Promise<HTMLImageElement>} A Promise that resolves with the loaded HTMLImageElement.
 */
export const loadImage = (uri) => {
  /**
   * @typedef {HTMLImageElement} HTMLImageElement - Represents an HTML <img> element.
   */

  return new Promise((resolve, reject) => {
    // Create a new Image element to load the image.
    let img = new Image()

    // When the image is successfully loaded, resolve the Promise with the loaded image.
    img.onload = () => {
      resolve(img)
    }

    // If there's an error loading the image, reject the Promise with the error.
    img.onerror = (error) => {
      reject(error)
    }

    // Set the 'src' attribute of the image to start loading it from the provided URI.
    img.src = uri
  })
}

/**
 * Checks if a given string is a valid stringified object in JSON format.
 * @param {string} str - The string to check.
 * @returns {boolean} Returns true if the input string is a valid stringified object, otherwise false.
 */
export const isStringifiedObject = (str) => {
  try {
    // Attempt to parse the input string as a JSON object.
    const parsedObject = JSON.parse(str)
    // Check if the parsed result is of type 'object' and not null.
    // This ensures that the string is a valid stringified object.
    return typeof parsedObject === "object" && parsedObject !== null
  } catch (error) {
    // If parsing fails, the input string is not a valid stringified object.
    return false
  }
}

/**
 * Generates a QR code image as a Data URL based on the given value.
 * @param {string} value - The data or value for which to generate the QR code.
 * @returns {Promise<string>} A Promise that resolves with the Data URL representing the QR code image.
 */
export const loadQR = async (value) => {
  /**
   * @typedef {string} DataURL - Represents a Data URL, which is a URL that embeds data directly in the URL.
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/Data_URIs Data URLs}
   */

  try {
    /**
     * The QRCode library is used to generate a Data URL for the given value.
     * @function QRCode.toDataURL
     * @param {string} value - The data or value for which to generate the QR code.
     * @returns {Promise<DataURL>} A Promise that resolves with the Data URL representing the QR code image.
     */

    let qrImage = await QRCode.toDataURL(value)

    // Return the generated Data URL representing the QR code image.
    return qrImage
  } catch (err) {
    // If there's an error during the QR code generation, log the error and return undefined.
    // console.error(err)
    return undefined
  }
}

/**
 * Function for fetching the status of the bulk template
 * @param {String} id bulk Template id
 * @returns {Object}
 */
export const fetchBulkStatus = async (id, email = false, emailRemove = false) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
      let token
      ApplicationState.subscribe((state) => (token = state.token))

      let url = `/api/swagger/v2/bulk/${id}`

      if (email) url = `/api/swagger/v2/bulk/${id}/emails`
      if (emailRemove) url = `/api/swagger/v2/bulk/${id}/emails?state=sent`

      // Send a GET request to the API to fetch the bulk template data
      const { data } = await axios.patch(url, null, {
        headers: {
          "x-access-token": token, // Include the access token in the request headers
        },
      })

      // Check if the response has an error and is not successful
      if (data.error && !data.success) {
        // Throw the error code returned by the API
        throw data.errorCode
      }
      resolve(data)
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Function for fetching the bulk Template
 * @param {String} id bulk template id
 * @returns {Object}
 */
export const fetchBulkTemplate = (id) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
      let token
      ApplicationState.subscribe((state) => (token = state.token))
      // Use the axios library to make a GET request to a specific API endpoint using the provided 'id'.
      const { data } = await axios.get(`/api/swagger/v2/bulk/${id}`, {
        headers: {
          // Include an authentication token from the $ApplicationState object in the request headers.
          "x-access-token": token,
        },
      })

      // Check if the response from the API contains an error and is not marked as successful.
      if (data.error && !data.success) {
        // If there's an error, throw the entire 'data' object, which likely contains error details.
        throw data.errorCode
      }

      resolve(data)
    } catch (error) {
      reject(error)
    }
  })
}

/**
 *
 * @param {String} id document template id
 * @returns {Object}
 */
export const fetchSelectedDocumentTemp = (id) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
      let token
      ApplicationState.subscribe((state) => (token = state.token))
      // Fetching document template by ID
      const { data } = await axios.get(`/api/swagger/v2/templates/documents/${id}`, {
        headers: {
          "x-access-token": token,
        },
      })

      // Check if the response has an error and is not successful
      if (data.error && !data.success) {
        throw data.errorCode // Throw the error code
      }

      resolve(data.template)
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Function for the fetching the email template associated with document template
 * @param {String} id email template id
 */
export const fetchEmailTemplate = async (id) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
      let token
      ApplicationState.subscribe((state) => (token = state.token))
      // Fetch the email template by ID using the axios library.
      const { data } = await axios.get(`/api/swagger/v2/templates/emails/${id}`, {
        headers: {
          // Include an authentication token from the $ApplicationState object in the request headers.
          "x-access-token": token,
        },
      })

      // Check if the response from the API contains an error and is not marked as successful.
      if (data.error && !data.success) {
        // If there's an error, throw the specific error code.
        throw data.errorCode
      }

      // console.log(data.templaste)

      resolve(data.template)
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Fucntion for sending the email to the respective mail ids
 * @param {Object} body email request body
 * @param {String} token JWT
 */
export const sendEmail = (body) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
      let token
      ApplicationState.subscribe((state) => (token = state.token))
      // Make a POST request to send the email using the axios library.
      const { data } = await axios.post(`/api/swagger/v2/email/send`, body, {
        headers: {
          // Include an authentication token from the $ApplicationState object in the request headers.
          "x-access-token": token,
        },
      })

      // Check if the response from the API contains an error and is not marked as successful.
      if (data.error && !data.success) {
        // If there's an error, throw the specific error code.
        throw data.errorCode
      }
      resolve()
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Function for releasing document
 * @param {String} id document id to be released
 * @param {String} token token
 * @returns
 */
export const releaseSelectedDocument = async (id) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
      let token
      ApplicationState.subscribe((state) => (token = state.token))
      const { data } = await axios.delete(`/api/swagger/v2/documents/${id}/release`, {
        headers: {
          "x-access-token": token,
        },
      })

      // Check for errors in the API response
      if (data.error && !data.success) {
        throw data.errorCode // Throw the error code
      }

      resolve()
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Fetches the file template data using an API call and processes the retrieved data.
 *
 * @param {String} id - file template id
 */
export const fetchFileTemplate = (id) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
      let token
      ApplicationState.subscribe((state) => (token = state.token))

      // Send a GET request to the API to fetch the file template data
      const { data } = await axios.get(`/api/swagger/v2/templates/files/${id}`, {
        headers: {
          "x-access-token": token, // Include the access token in the request headers
        },
      })

      // Check if the response has an error and is not successful
      if (data.error && !data.success) {
        throw data.errorCode // Throw the error code returned by the API
      }

      resolve(data.template)
    } catch (error) {
      reject(error)
    }
  })
}

// /**
//  * @description Fetches the list of document templates.
//  */
// export const getDocumentsList = async () => {
//   return new Promise(async (resolve, reject) => {
//     try {
//       if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
//       let token
//       ApplicationState.subscribe((state) => (token = state.token))
//       // Fetching list of document templates
//       const { data } = await axios.get(`/api/swagger/v2/templates/documents`, {
//         headers: {
//           "x-access-token": token,
//         },
//       })

//       // Check if the response has an error and is not successful
//       if (data.error && !data.success) {
//         throw data.errorCode // Throw the error code
//       }

//       resolve(data.templates)
//     } catch (error) {
//       reject(error)
//     }
//   })
// }

export const createNewBulkSession = (reqBody) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
      let token
      ApplicationState.subscribe((state) => (token = state.token))
      // Send a POST request to the API endpoint
      const { data } = await axios.post(`/api/swagger/v2/bulk`, reqBody, {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-access-token": token, // Include the access token in the request header
        },
      })

      // Check if the response has an error and is not successful
      if (data.error && !data.success) {
        throw data.errorCode // Throw the error code if there's an error
      }

      resolve(data)
    } catch (error) {
      reject(error)
    }
  })
}

export const createNewBulkSessionTest = (reqBody) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
      let token
      ApplicationState.subscribe((state) => (token = state.token))
      // Send a POST request to the API endpoint
      const { data } = await axios.post(`/api/swagger/v2/bulk/`, reqBody, {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-access-token": token, // Include the access token in the request header
        },
      })

      // Check if the response has an error and is not successful
      // if (data.error && !data.success) {
      //   throw data.errorCode // Throw the error code if there's an error
      // }

      resolve(data)
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Function for fetching the bulk template
 * @param {String} id bulk template id
 */
export const checkBulkTemplate = (id) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
      let token
      ApplicationState.subscribe((state) => (token = state.token))
      const { data } = await axios.get(`/api/swagger/v2/bulk/${id}`, {
        headers: {
          "x-access-token": token,
        },
      })

      // Check if the response has an error and is not successful
      if (data.error && !data.success) {
        throw data.errorCode
      }

      resolve(data)
    } catch (error) {
      reject(error)
    }
  })
}

export const sendXlFile = (file, id) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
      let token
      ApplicationState.subscribe((state) => (token = state.token))
      const { data } = await axios.post(
        `/api/swagger/v2/bulk/${id}`,
        {
          file: file,
        },
        {
          progress: function (progressEvent) {
            let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          },
          onUploadProgress: function (progressEvent) {
            let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          },
          headers: {
            "Content-Type": "multipart/form-data",
            "x-access-token": token,
          },
        }
      )

      // Check if the response has an error and is not successful
      if (data.error && !data.success) {
        throw data.errorCode // Throw the error code
      }
      resolve(data)
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Function for the checking whether the file is in the extension of
 * .jpg,.png or not using magic number
 * @param {File} file - the file to be validated
 */
export const validateFile = (file) => {
  return new Promise((resolve, reject) => {
    // Create a new instance of FileReader
    const filereader = new FileReader()

    // Define the callback function for when the file is loaded
    filereader.onloadend = (e) => {
      // Extract the first 4 bytes of the file as an Uint8Array
      const arr = new Uint8Array(e.target.result).subarray(0, 4)
      let header = ""

      // Iterate over the bytes and convert them to hexadecimal representation
      for (let i = 0; i < arr.length; i++) {
        header += arr[i].toString(16)
      }
      // Check if the header matches the expected values for JPEG or PNG
      if (header === "89504e47" || header === "ffd8ffee" || header === "ffd8ffe0" || header === "ffd8ffe1" || header === "ffd8ffe2" || header === "ffd8ffe8") {
        // If the header matches either format, resolve the Promise with the file object
        resolve(file)
      } else {
        // If the header doesn't match, reject the Promise with an error message
        reject("Invalid Format")
      }
    }

    // Read the first 4 bytes of the file as an ArrayBuffer
    filereader.readAsArrayBuffer(file.slice(0, 4))
  })
}

/**
 * @description Fetches the list of document templates.
 * @param {number} page page number of the document template list
 */
export const getDocumentsList = async (page) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
      let token
      ApplicationState.subscribe((state) => (token = state.token))
      // Fetching list of document templates
      const { data } = await axios.get(`/api/swagger/v2/templates/documents?limit=10&page=${page}&sort=-1&sortBy=date`, {
        headers: {
          "x-access-token": token,
        },
      })

      // Check if the response has an error and is not successful
      if (data.error && !data.success) {
        throw data.errorCode // Throw the error code
      }

      resolve(data)
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Function to generate and download an Excel file.
 * @param header represents the header row of the excel file
 * @param ValuesArr respective values row for the header
 */
export const generateXlFile = async (header, ValuesArr, id) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
      // Create a new ExcelJS workbook and add a worksheet named "Sheet1".
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet("Sheet1")

      // Always mention rowheight and colWidth, Omitting one property will throws error
      worksheet.properties = {
        defaultRowHeight: 15,
        defaultColWidth: 30,
      }

      // Configure the worksheet views with freezing the first row
      worksheet.views = [
        {
          state: "frozen",
          xSplit: 0,
          ySplit: 1, // Specify the row index to freeze (in this case, the first row)
          topLeftCell: "A2", // Specify the cell below the frozen row
          activeCell: "A2", // Specify the active cell after freezing
        },
      ]

      // Add the provided `header` array as the first row in the worksheet.
      const headerRow = worksheet.addRow(header)
      headerRow.font = {
        bold: true,
      }

      // Loop through each array of values (`ValuesArr`) and add them as rows in the worksheet.
      for (let i = 0; i < ValuesArr.length; i++) {
        worksheet.addRow(ValuesArr[i])
      }

      // Generate the Excel file and get its binary data as a buffer.
      const buffer = await workbook.xlsx.writeBuffer()

      // Create a Blob from the buffer, specifying the MIME type as Excel.
      let blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })

      // Create a link element to simulate a click event and download the Excel file.
      let link = document.createElement("a")
      link.href = URL.createObjectURL(blob)

      // Specify the download filename for the Excel file.
      link.download = `BulkUpload-${id}`

      // Simulate a click on the link to trigger the download of the Excel file.
      link.click()
      resolve()
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Function to fetch a single font using its ID
 * @param {String} fontID id for the font family
 */
export const fetchSingleFont = (fontID) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
      let token
      ApplicationState.subscribe((state) => (token = state.token))

      // Make a GET request to the API to fetch font data
      const { data } = await axios.get(`/api/swagger/v2/fonts/${fontID}`, {
        headers: {
          "x-access-token": token,
        },
      })

      // Check for errors in the API response
      if (data.error && !data.success) {
        throw data.errorCode // Throw the error code if the API call was not successful
      }

      resolve(data.font)
    } catch (error) {
      reject(error)
    }
  })
}
